/**
 * Brand Configuration for Web App
 * 
 * This file initializes the brand configuration for the web application.
 * It should be called during app initialization to set up the design tokens.
 */

import { initializeBrand, getRestaurantId, setRestaurantId, generateCSSVariables, RestaurantService, getRestaurantName, getRestaurantLogoUrl } from '@restaurant/core';
import { safeGetLogger } from '@restaurant/logging';


/** Regenerate CSS variables from the current brand config and (re)inject
 * them into the #brand-tokens style element. Safe to call repeatedly —
 * used at boot and by the showcase live-customize listener. */
export function injectBrandTokens(): void {
  const cssVariables = generateCSSVariables()
  let styleElement = document.getElementById('brand-tokens')
  if (!styleElement) {
    styleElement = document.createElement('style')
    styleElement.id = 'brand-tokens'
    document.head.appendChild(styleElement)
  }
  styleElement.textContent = cssVariables
}

/**
 * Initialize brand for the web application
 * This function:
 * 1. Initializes brand configuration via Flask API/Supabase
 * 2. Sets the design tokens
 * 3. Injects CSS variables into the document
 */
export async function initializeWebBrand(restaurantService?: RestaurantService, businessId?: string): Promise<void> {
  try {
    // Use provided business ID (from tenant resolution)
    const restaurantId = businessId

    // Initialize brand with RestaurantService to load from Flask API
    await initializeBrand(restaurantService, restaurantId);

    // Generate and inject CSS variables
    injectBrandTokens();

    // Add restaurant ID as a data attribute for debugging/styling
    document.documentElement.setAttribute('data-restaurant', getRestaurantId());

    // Update document title and favicon to match the tenant
    const restaurantName = getRestaurantName();
    document.title = restaurantName;

    const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (appleTitle) appleTitle.setAttribute('content', restaurantName);

    const logoUrl = getRestaurantLogoUrl();
    if (logoUrl) {
      // Update favicon link elements to use the restaurant's logo
      const iconSelectors = [
        'link[rel="icon"][type="image/svg+xml"]',
        'link[rel="icon"][type="image/png"]',
        'link[rel="shortcut icon"]',
        'link[rel="apple-touch-icon"]',
      ];
      for (const selector of iconSelectors) {
        const el = document.querySelector(selector);
        if (el) el.setAttribute('href', logoUrl);
      }
    }

    safeGetLogger()?.config('brand_loaded', { name: getRestaurantId(), source: 'api' });
  } catch (error) {
    safeGetLogger()?.config('brand_fallback', { reason: 'api_failed', error: String(error) });

    // Fallback: still try to generate CSS variables
    try {
      injectBrandTokens();
      document.documentElement.setAttribute('data-restaurant', getRestaurantId());
    } catch (fallbackError) {
      safeGetLogger()?.error('Complete brand initialization failure', { domain: 'config', error: String(fallbackError) });
    }
  }
}

/**
 * Update brand configuration dynamically
 * Useful for development/testing different brand configurations
 */
export async function updateBrand(restaurantId: string): Promise<void> {
  if (import.meta.env.DEV) {
    setRestaurantId(restaurantId);
    await initializeWebBrand(undefined, restaurantId);

    window.dispatchEvent(new CustomEvent('brand-changed', { detail: { restaurantId } }));
  } else {
    safeGetLogger()?.warn('Brand updates are only available in development mode', { domain: 'config' });
  }
}